import React, { useState, useRef, useEffect } from 'react';
import _ from 'lodash';
import propTypes from 'prop-types';
import classNames from 'classnames';
import { useBreakpointChange } from '_ui-core-js/React/hooks/useBreakpointChange.js';
import { multiplier } from '_ui-core-js/utilities/multiplier.js';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { useDragScroll } from '_ui-core-js/React/hooks/useDragScroll.js';
import { subscribe, unsubscribe} from '_ui-core-js/utilities/events.js';
import { VALUES_MAP } from '../valuesMap.js';
import { ColorOption } from './ColorOption.jsx';
import { UpholsteryOption } from './UpholsteryOption.jsx';
import { AccessoryOption } from './AccessoryOption.jsx';
import { WheelOption } from './WheelOption.jsx';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities/fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { RANGE_GTM_TRACKING_VALUES } from '../../../../../../range/ui/constants/analytics.js';

export function Toolbar(props) {
  const isMountedRef = useRef(false);
  const toolbarRef = useRef();
  const optionsSelectorRef = useRef();
  const [activeOptions, setActiveOptions] = useState(null); // colors, upholsteries, accessories, tyres
  const [displayName, setDisplayName] = useState('');
  const [toolBarMode, setToolBarMode] = useState(VALUES_MAP.MODES.customise[0]);
  // useBreakpointChange - when it is called it returns whether the current breakpoint value larger than given breakpoint.
  // Use this to set inital state with 2nd item in returned array. 
  // The effect will then be also registered to run everytime the breakpoint changes and we pass
  // it a callback to set a new state everytime.
  const [smallView, setSmallView] = useState(!useBreakpointChange((breakpoint, largerThanPivotBp) => {
    setSmallView(!largerThanPivotBp);
  }, 'md' )[1]);

  const block='c-toolbar';
  const validOptions = _.omitBy(props.options, _.isUndefined);
  const details = Object.keys(props.details);
  const options = Object.keys(validOptions);
  const optionsData = activeOptions ? props.options[activeOptions]: undefined;
  const activeSelections = props.activeSelections;
  const customiseMode = toolBarMode === VALUES_MAP.MODES.customise[0];
  const detailsMode = toolBarMode === VALUES_MAP.MODES.details[0];
  const dragEvents = useDragScroll();

  const optionHasData = index => {
    const optionObj = props.options[options[index]];
    return !!optionObj;
  };

  function closeOptions() {
    setActiveOptions(null);
  };

  useEffect(() => {
    subscribe('kc-image-rotation-active', closeOptions);
    isMountedRef.current = true;
  
    return () => {
      unsubscribe('kc-image-rotation-active', closeOptions);
      isMountedRef.current = false;
    }
  }, []);

  // When receiving new props and display name is not set make sure to update display name.
  useEffect(() => {
    if (activeOptions && optionsData) {
      const modelObj = optionsData[activeSelections[activeOptions]];
      if (modelObj) {
        setDisplayName(getDisplayName(modelObj));
      } else {
        setDisplayName('');
      }  
    }
  }, [activeSelections]);

  const getDisplayName = modelObj => modelObj.name || modelObj['local-name'] || modelObj['global-name'];

  function toggleOptionSelector(options) {
    options = options === activeOptions ? null : options; // toggle open/close
    setActiveOptions(options);
    // Multiselect (eg. accessories) has default set to undefined as there isnt one option active at a time. 
    // Therefore if multiseelct set display name to empty string else set to active option.
    if (options) {
      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
        componentName: props.baseModelName ? RANGE_GTM_TRACKING_VALUES.componentName.gradeRange + '-' + props.baseModelName : RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
        componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
        action: RANGE_GTM_TRACKING_VALUES.action.expandFeature,
        label: props.modelName
      })
    }

    if (Number.isInteger(activeSelections[options])) {
      const modelObj = props.options[options][activeSelections[options]];
      setDisplayName(getDisplayName(modelObj));
    } else {
      setDisplayName('');
    }
  };

  function updateDisplayName(index, isMultiSelect) {
    if (Number.isInteger(index)) {
      // set temporarily on mouseenter
      const modelObj = optionsData[index];
      setDisplayName(getDisplayName(modelObj));
    } else {
      // reset to active value on mouseleave
      if (!isMultiSelect) {
        const modelObj = optionsData[activeSelections[activeOptions]];
        setDisplayName(getDisplayName(modelObj));
      } else {
        setDisplayName('');
      }
    }
  };

  function handleKeyDown(event, type, payload) {
    if (event.key === 'Enter') {
      if (type === 'options-open') {
        toggleOptionSelector(payload);
      }
      else if (type === 'mode-switch') {
        setToolBarMode(payload)
      }
    }
  };

  // Extracted this snippet into its own variable as it needs to be rendered in different places depending on the viewport size.
  const OptionSelectors = (isActive=false, modifier) => ( 
    <CssAnimation 
    enter={{ name: 'short-slide-up-fade-in', duration: 0.5 }} 
    exit={{ name: 'short-slide-down-out-fade-out', duration: 0.5 }}
    triggerEnter={isActive} 
    triggerExit={!isActive}
    disableExit={smallView}
    renderView={isActive}
    removeViewOnExit={true}
    >
      <div 
      ref={optionsSelectorRef} 
      key={modifier} 
      className={`${block}__option-selector-container ${block}__option-selector-container--${modifier}`}
      >
        <div className={`${block}__option-display-name h6`}>{ displayName }</div>
        <div className={`${block}__option-selector-scroll-container`} {...dragEvents}>
        { 
          activeOptions && optionsData &&
          (optionsData.map((option, index) => (
            activeOptions === VALUES_MAP.OPTIONS.colors[1] ? 
              <ColorOption 
              block={block} 
              color={option} 
              index={index} 
              active={index === activeSelections[activeOptions]}
              updateDisplayName={updateDisplayName}
              imageProps={props.imageProps} 
              key={`color-value-${index}`}
              modelName={props.modelName}
              baseModelName={props.baseModelName}
              /> 
            :
            activeOptions === VALUES_MAP.OPTIONS.upholsteries[1] ? 
              <UpholsteryOption 
              block={block} 
              upholstery={option} 
              index={index} 
              active={index === activeSelections[activeOptions]}
              updateDisplayName={updateDisplayName} 
              imageProps={props.imageProps} 
              key={`upholstery-value-${index}`}
              modelName={props.modelName}
              baseModelName={props.baseModelName}
              /> 
            :
            activeOptions === VALUES_MAP.OPTIONS.wheels[1] ? 
              <WheelOption 
              block={block} 
              wheel={option} 
              index={index}
              active={index === activeSelections[activeOptions]}
              updateDisplayName={updateDisplayName} 
              key={`wheel-value-${index}`}
              modelName={props.modelName}
              baseModelName={props.baseModelName}
              /> 
            :
            activeOptions === VALUES_MAP.OPTIONS.accessories[1] ? 
              <AccessoryOption 
              block={block} 
              accessory={option} 
              index={index}
              active={activeSelections[activeOptions].indexOf(index) > -1} 
              updateDisplayName={updateDisplayName} 
              key={`accessory-value-${index}`}
              modelName={props.modelName}
              baseModelName={props.baseModelName}
              /> 
            :
            null
          )))
        }
        </div>
      </div>
    </CssAnimation>
  );

  return (
    <div className={block} ref={toolbarRef}>
       {/* Render here on large view only */}
       { OptionSelectors(activeOptions && optionsData && !smallView && customiseMode, 'large-view') }
      <div className={`${block}__tabs-container`} aria-role='tablist'>
        { 
          // Iterate 5 items - 4 options/details tabs + the toolbar mode sitch tab
          [...Array(options.length)].map((_, index, {length}) => (
            
            // This is for the inital toolbar animation where the tabs slide up and fade-in in sequence.
            // It only runs when the component mounts and is disabled after mounting via the disable prop.
            <CssAnimation
            enter={{ 
              name: 'short-slide-up-fade-in', 
              duration: 0.15,  
              delay: `${multiplier(index, 0.10, 1)}, hidden` 
            }}
            triggerEnter={true}
            disable={isMountedRef.current} 
            key={`options-${index}`}
            >
              <div 
              key={`${index}-tab`}
              className={
                classNames({
                  [`${block}__tab`]: true,
                  [`${block}__tab--non-interactive`]: detailsMode && index + 1 !== length,
                  [`${block}__tab--active`]: customiseMode && optionsData && options[index] === activeOptions,
                  [`${block}__tab--disabled`]: (customiseMode && !optionHasData(index) && (index + 1 !== length)) || (detailsMode && props.details[details[index]] === '-')
                })}  
              >
                {/* Details tab content */}
                <CssAnimation
                enter={{name: 'short-slide-up-fade-in', duration: 0.15, delay: `${multiplier(index, 0.10)}, hidden`}}
                exit={{name: 'short-slide-up-out-fade-out', duration: 0.15, delay: `${multiplier(index, 0.10)}`}}
                triggerEnter={detailsMode}
                triggerExit={!detailsMode}
                removeViewOnExit={true}
                >
                { index !== length ? 
                    // Details content (price, size, seats, fuel) - render when not last item
                    <div key={details[index]} className={`${block}__details-tab ${block}__details-tab--${details[index]}`}>
                      <div className={`${block}__details-tab-text-content ${block}__details-tab-text-content--${details[index]}`}>
                        <span className={`${block}__details-tab-label ${block}__details-tab-label--${details[index]} small-text`}>
                          {props.labels[details[index]]}
                        </span>
                        <span className={`${block}__details-tab-value h6`}>
                          {props.details[details[index]]}
                        </span>
                      </div>
                    </div>  
                    :
                    // View customise tab (toggle button) - render when last item
                    <div 
                    key={details[index]}  
                    className={`${block}__mode-switch-tab`} 
                    role='tab'
                    tabIndex='0'
                    onClick={() => {
                      fireGTMTrackingEvent({
                        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
                        componentName: props.baseModelName ? RANGE_GTM_TRACKING_VALUES.componentName.gradeRange + '-' + props.baseModelName : RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
                        componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
                        action: CORE_GTM_TRACKING_VALUES.action.clickButton,
                        label: RANGE_GTM_TRACKING_VALUES.label.customize + '-' + props.modelName
                      })

                      setToolBarMode(VALUES_MAP.MODES[toolBarMode][1])}
                    }
                    onKeyDown={e => handleKeyDown(e, 'mode-switch', VALUES_MAP.MODES[toolBarMode][1])}
                    > 
                      <span className={`${block}__mode-switch-tab-label ${block}__mode-switch-tab-label--${VALUES_MAP.MODES.details[0]} h6`}>
                        {props.labels[VALUES_MAP.MODES.details[1]]}
                      </span>
                    </div>
                }
                </CssAnimation>

                {/* Customise tab content */}
                <CssAnimation
                enter={{ name: 'short-slide-up-fade-in', duration: 0.15,  delay: `${multiplier(index, 0.10, isMountedRef.current ? 0 : 1)}, hidden` }}
                exit={{name: 'short-slide-up-out-fade-out', duration: 0.15,  delay: `${multiplier(index, 0.10, isMountedRef.current ? 0 : 1)}`}}
                triggerEnter={customiseMode}
                triggerExit={!customiseMode}
                removeViewOnExit={true}
                
                >
                { index !== length ? 
                    // Customise option content (colors, upholsteries, wheels, accessories) - render when not last item
                    <div  
                    key={options[index]}
                    className={
                      classNames({
                        [`${block}__options-category-tab`]: true,
                        [`${block}__options-category-tab--${options[index]}`]: true,
                        [`${block}__options-category-tab--active`]: optionsData && options[index] === activeOptions
                      })}  
                    role='tab'
                    tabIndex='0'
                    aria-selected={options[index] === activeOptions}
                    onClick={() => toggleOptionSelector(options[index])}
                    onKeyDown={e => handleKeyDown(e, 'options-open', options[index])}
                    >
                      <span className={`${block}__options-category-tab-label ${block}__options-category-tab-label--${options[index]} h6`}>
                        {props.labels[options[index]]}
                      </span>
                    </div>
                    :
                    null
                    // View car details tab (toggle button) - render when last item - disable not wanted during this campaign
                    /*<div 
                    key={options[index]} 
                    className={`${block}__mode-switch-tab`} 
                    role='tab'
                    tabIndex='0'
                    onClick={() => {
                      fireGTMTrackingEvent({
                        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
                        componentName: props.baseModelName ? RANGE_GTM_TRACKING_VALUES.componentName.gradeRange + '-' + props.baseModelName : RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
                        componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
                        action: CORE_GTM_TRACKING_VALUES.action.clickButton,
                        label: RANGE_GTM_TRACKING_VALUES.label.viewSpecs + '-' + props.modelName
                      })

                      setToolBarMode(VALUES_MAP.MODES[toolBarMode][1])}
                    }
                    onKeyDown={e => handleKeyDown(e, 'mode-switch', VALUES_MAP.MODES[toolBarMode][1])}
                    > 
                      <span className={`${block}__mode-switch-tab-label ${block}__mode-switch-tab-label--${VALUES_MAP.MODES.customise[0]} h6`}>
                        {props.labels[VALUES_MAP.MODES.customise[1]]}
                      </span>
                    </div> */
                }
                </CssAnimation>

                {/* Render here on small view only */}
                { OptionSelectors((options[index] === activeOptions) && optionsData && smallView && customiseMode, 'small-view') }
              </div>
            </CssAnimation>
          ))
        }
      </div>
    </div>
  )
};

Toolbar.propTypes = {
  activeSelections: propTypes.object.isRequired,
  imageProps: propTypes.object.isRequired,
  labels: propTypes.object.isRequired,
  options: propTypes.object.isRequired,
  modelName: propTypes.string,
  baseModelName: propTypes.string
};
